import type { ColumnDef, MeterWithValueProps } from '@talos/kyoko';
import {
  Box,
  DistributionBar,
  getOrderStatusText,
  includesEnumValue,
  Meter,
  orderStatusTextToColor,
  QuoteStatusEnum,
  toBigWithDefault,
  type DistributionNamedSection,
} from '@talos/kyoko';
import type { IAggFuncParams, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import Big from 'big.js';
import { isCareOrderRow, isLegRow, isOrderRow, isQuoteRow, type CareOrderBlotterEntity, type QuoteRow } from '../types';
import { childRowsToMeterBars, type CareOrderProgressBar } from '../utils';
import { legRowFadedCellClassRules } from './legRowFadedCellClassRules';

export const CARE_ORDER_PROGRESS_AGG_FUNC = 'careOrderProgress';

function getQuoteMeterValue(quoteStatus: QuoteStatusEnum) {
  return {
    value: includesEnumValue([QuoteStatusEnum.Filled, QuoteStatusEnum.PendingFix], quoteStatus) ? 1 : 0,
    color: quoteStatus === QuoteStatusEnum.Filled ? 'var(--colors-green-lighten)' : 'var(--colors-blue-lighten)',
  };
}

function getQuoteProgressValue(quote: QuoteRow) {
  return {
    data: quote,
    meter: getQuoteMeterValue(quote.QuoteStatus),
  };
}

export const careOrderProgressAggFunc = (params: IAggFuncParams) => {
  if (params.data == null || params.values.length === 0) {
    return [];
  }
  if (isCareOrderRow(params.data)) {
    return childRowsToMeterBars(params.values, params.data.OrderQty);
  }
  if (isQuoteRow(params.data)) {
    return getQuoteProgressValue(params.data);
  }
  throw new Error(`unexpected row type`, params.data);
};

export const getCareOrderProgressColumn = (): ColumnDef<CareOrderBlotterEntity> => {
  return {
    type: 'custom',
    id: 'progress',
    pinned: 'left',
    initialAggFunc: CARE_ORDER_PROGRESS_AGG_FUNC,
    params: {
      valueGetter: (
        params: ValueGetterParams<CareOrderBlotterEntity>
      ): { data: CareOrderBlotterEntity; meter: MeterWithValueProps } | unknown[] => {
        if (params.node == null) {
          throw new Error('missing node in valueGetter');
        }

        // This runs for cases where the CO doesn't have any children (either loaded or created) yet, so AgGrid decides to use the valueGetter instead of the aggFunc.
        // If the CO has no children, then we render empty.
        if (isCareOrderRow(params.data)) {
          return [];
        }

        if (isQuoteRow(params.data)) {
          return getQuoteProgressValue(params.data);
        }

        if (isOrderRow(params.data)) {
          const order = params.data;
          const status = getOrderStatusText({
            type: 'orderLike',
            order,
            isCustomerOrder: false,
          });
          return {
            data: order,
            meter: {
              value: (toBigWithDefault(params.data.OrderQty, 0).gt(0)
                ? Big(params.data.CumQty ?? '0').div(params.data.OrderQty)
                : Big(0)
              ).toNumber(),
              color: orderStatusTextToColor(status, params.context.current.theme),
            },
          };
        }

        if (isLegRow(params.data)) {
          return {
            data: params.data,
            meter: getQuoteMeterValue(params.data.QuoteStatus),
          };
        }

        throw new Error(`unexpected row type ${(params.data as any)?.constructor.name}`);
      },
      valueFormatter: (params: ValueFormatterParams<CareOrderBlotterEntity>) => {
        if (isCareOrderRow(params.data)) {
          const orderQtyBig = toBigWithDefault(params.data.OrderQty, 0);
          if (orderQtyBig.eq(0)) {
            return '0%';
          }

          return `${toBigWithDefault(params.data.CumQty, 0).div(orderQtyBig).times(100).toFixed(2)}%`;
        }

        if (isQuoteRow(params.data) || isLegRow(params.data)) {
          const meterValue = getQuoteMeterValue(params.data.QuoteStatus).value;
          return `${Big(meterValue).times(100).toFixed(2)}%`;
        }

        return '';
      },
      cellClassRules: legRowFadedCellClassRules,
      cellRenderer: AgCareOrderProgress,
      minWidth: 120,
      maxWidth: 480,
      width: 120,
    },
  };
};

function AgCareOrderProgress(params: ICellRendererParams<CareOrderBlotterEntity>) {
  if (isCareOrderRow(params.data)) {
    const bars: CareOrderProgressBar[] = params.value;
    const sections: DistributionNamedSection[] = bars
      .filter(bar => bar.valueNotional.gt(0))
      .map(bar => ({
        id: bar.id,
        header: null,
        parts: [
          {
            id: bar.id,
            color: bar.color,
            value: bar.valuePercent,
            appearance: bar.appearance,
          },
        ],
      }));

    return (
      <Box w="100%">
        <DistributionBar sections={sections} showHeaders={false} showRemainder={true} showLabel={true} size="small" />
      </Box>
    );
  }

  return <Meter label={params.valueFormatted!} {...params.value.meter} showInitialAnimation={false} animate={false} />;
}
